<template>
  <div :id="id">
    <div
      v-if="!minimize"
      :class="['pf-annotation-callout--text', bubbleStyles]"
      :style="inlineStyles"
      ref="container"
    >
      <pf-handler
        ref="handle"
        v-if="configuration"
        v-bind="handleProps"
        @pftracking="updateDynamic"
        @pfmoveevent="updateMove"
        @pfresize="scale"
      ></pf-handler>
      <div
        :class="['pf-annotation-callout--text__span-container', alignClass]"
        ref="calloutText"
        @click="configuration ? clicked($event) : null"
      >
        <span
          :class="['pf-annotation-callout--text__span', spanClass]"
          :contenteditable="editing"
          @focus="focusInput"
          @input="editText"
          @blur="updateText"
        >
          <component class="pf-annotation-callout--text__span-title" :is="headingType">{{
            innerText
          }}</component>
        </span>
      </div>
    </div>
  </div>
</template>

<script>
  import PfHandler from './Handler';
  import PfMiniTag from './MiniTag';
  import repoMixin from '../../mixins/interactions/reposition';

  export default {
    name: 'PfCalloutText',
    components: {
      PfHandler,
      PfMiniTag,
    },
    mixins: [repoMixin],
    props: {
      id: {
        type: String,
      },
      alignment: {
        type: String,
        default: 'left',
        validator(value) {
          return ['left', 'center', 'right'].indexOf(value) !== -1;
        },
      },
      bold: {
        type: Boolean,
        default: false,
      },
      bounds: {
        type: Object,
      },
      configuration: {
        type: Boolean,
      },
      headingLevel: {
        type: Number,
        default: 6,
        validator(value) {
          return value >= 1 && value <= 6;
        },
      },
      content: {
        type: String,
        default: '[Click to edit]',
      },
      bandX: {
        type: Array,
      },
      bubble: {
        type: Boolean,
        default: false,
      },
      domainX: {
        type: Array,
        required: true,
      },
      domainY: {
        type: Array,
        required: true,
      },
      editable: {
        type: Boolean,
        default: false,
      },
      inFocus: {
        type: Boolean,
        default: false,
      },
      minimize: {
        type: Boolean,
        default: false,
      },
      plot: {
        type: Object,
      },
      x: {
        type: Number,
        required: true,
      },
      y: {
        type: Number,
        required: true,
      },
      z: {
        type: Number,
        default: 1,
      },
      rangeX: {
        type: Array,
        required: true,
      },
      rangeY: {
        type: Array,
        required: true,
      },
      transpose: {
        type: Boolean,
        default: false,
      },
      scaleTypeX: {
        type: String,
        default: 'linear',
      },
      scaleTypeY: {
        type: String,
        default: 'linear',
      },
      selected: {
        type: Boolean,
        default: false,
      },
      paddingInner: {
        type: Number,
        default: 0.1,
      },
      width: {
        type: Number,
        default: 150,
      },
      yAxisWidth: {
        type: Number,
        default: 86,
      },
    },
    computed: {
      bubbleStyles() {
        const obj = {};
        obj['pf-annotation-callout--configuration'] = this.configuration;
        obj['pf-annotation-callout--text__bubble'] = this.bubble;
        obj['pf-annotation--selected'] = this.selected;

        return obj;
      },
      containerRef() {
        return this.$refs ? this.$refs.calloutText : null;
      },
      headingType() {
        return `h${this.headingLevel}`;
      },
      inlineStyles() {
        return `top: ${this.pointY}px; left: ${this.pointX}px; z-index: ${this.z}; width: ${this.spanWidth}px;`;
      },
      miniStyles() {
        return `top: ${this.pointY}px; left: ${this.pointX}px; z-index: ${this.z};`;
      },
      alignClass() {
        const obj = {};

        obj['pf-annotation-callout--text__span-left'] = this.alignment === 'left';
        obj['pf-annotation-callout--text__span-center'] = this.alignment === 'center';
        obj['pf-annotation-callout--text__span-right'] = this.alignment === 'right';

        return obj;
      },
      spanClass() {
        const obj = {};

        obj['pf-annotation-callout--text__span-bold'] = this.boldText;

        return obj;
      },
      boldText() {
        return `${this.bold}` === 'true';
      },
      height() {
        return this.$refs.container
          ? this.$refs.container.$el.getBoundingClientRect().height
          : null;
      },
      pointX() {
        const offset = this.scaleTypeX !== 'band' ? this.yAxisWidth : 0;
        // left is offset between absolute positioning and svg by axis width
        const x =
          this.configuration && this.dynamicPos != null && this.dynamicPos.x != null
            ? this.dynamicPos.x
            : this.plotX + offset;
        const isLeft = x < this.yAxisWidth;
        const rightBound = parseFloat(this.bounds.right);
        const isRight = parseFloat(x) + parseFloat(this.width) > rightBound;
        return isLeft ? Math.max(this.yAxisWidth, x) : isRight ? rightBound - this.width : x;
      },
      pointY() {
        const y =
          this.configuration && this.dynamicPos != null && this.dynamicPos.y != null
            ? this.dynamicPos.y
            : this.plotY;
        const isTop = y < 0;

        return isTop ? 0 : y;
      },
      plotX() {
        return this.plot != null ? this.getScaleX(this.plot.x) : this.x;
      },
      plotY() {
        return this.plot != null ? this.yScale(this.plot.y) : this.y;
      },
      handleProps() {
        return {
          scaleTypeX: this.scaleTypeX,
          scaleTypeY: this.scaleTypeY,
          domainX: this.domainX,
          domainY: this.domainY,
          bandX: this.bandX,
          rangeX: this.rangeX,
          rangeY: this.rangeY,
          x: this.pointX,
          y: this.pointY,
          width: this.spanWidth,
          yAxisWidth: this.yAxisWidth,
          paddingInner: this.paddingInner,
          transpose: this.transpose,
        };
      },
    },
    data() {
      return {
        allowUpdate: false,
        editing: false,
        spanWidth: this.width,
        draftText: this.content,
        innerText: this.content,
        dynamicPos: null,
        deltaWidth: 0,
      };
    },
    updated() {
      this.innerText = this.draftText;
    },
    watch: {
      editable: {
        deep: true,
        handler(val) {
          this.editing = val;
        },
      },
      selected: {
        deep: true,
        handler(val) {
          if (!val) {
            this.editing = false;
          }
        },
      },
      transpose(val) {
        this.transposeScale = val;
      },
    },
    methods: {
      clicked(e) {
        this.trap(e);
        if (!this.selected) {
          this.$emit('click');
        }
        this.editing = true;
      },
      editText(e) {
        this.draftText = e.target.innerText.trim();
        this.allowUpdate = true;
      },
      focusInput() {
        this.emitProp('inFocus', true);
      },
      updateText() {
        if (!this.allowUpdate) {
          return;
        }

        this.emitProp('content', this.draftText);
        this.emitProp('inFocus', false);
        this.allowUpdate = true;
      },
      updateDynamic(e) {
        this.dynamicPos = e;
      },
      updateMove(e) {
        this.$emit('pfmoveevent', e);
      },
      scale(e) {
        if (this.$refs.handle && !this.spanWidth) {
          this.spanWidth = this.$refs.handle.$el.getBoundingClientRect().width;
        }
        this.spanWidth += e.width;
        this.deltaWidth += e.width;
        this.emitProp('width', this.spanWidth);
      },
    },
    mounted() {
      this.transposeScale = this.transpose;
      if (this.configuration && !this.plot) {
        this.updatePosition('plot', { x: this.pointX, y: this.pointY });
      }
    },
  };
</script>

<style lang="scss">
  @import '@mce/visual-language';
  @import '@mds/constants';
  @import '@mds/typography';

  .pf-annotation-callout--text {
    position: absolute;
    color: $mce-hover-flag-text-color;
    padding: $mds-space-half-x;

    &__span {
      white-space: pre-line;

      &-container {
        position: relative;

        .pf-annotation-callout--configuration &:hover {
          background-color: rgba($mds-interactive-color-primary-default, 0.2);
        }
      }

      cursor: text;

      &-left {
        text-align: left;
      }

      &-center {
        text-align: center;
      }

      &-right {
        text-align: right;
      }

      h1 {
        @include mds-level-1-heading();

        margin: 0;
      }

      h2 {
        @include mds-level-2-heading();

        margin: 0;
      }

      h3 {
        @include mds-level-3-heading();

        margin: 0;
      }

      h4 {
        @include mds-level-4-heading();

        margin: 0;
      }

      h5 {
        @include mds-level-5-heading();

        margin: 0;
      }

      h6 {
        @include mds-level-6-heading();

        margin: 0;
      }

      // TODO: safe to remove once PF-1004 is played
      /* stylelint-disable-next-line selector-type-no-unknown */
      h7 {
        @include mds-body-text-m();

        margin: 0;
      }

      // TODO: safe to remove once PF-1004 is played
      /* stylelint-disable-next-line selector-type-no-unknown */
      h8 {
        @include mds-body-text-s();

        margin: 0;
      }

      &-bold &-title {
        font-weight: 600;
      }
    }

    &__bubble {
      border: $mce-hover-flag-border;
      border-radius: $mds-border-radius-panel;
      box-shadow: $mce-hover-flag-box-shadow-drop;
      background-color: $mce-hover-flag-background-color;
    }
  }

  // .pf-annotation-callout--text__bubble {
  //   border: $mce-hover-flag-border;
  //   border-radius: $mds-border-radius-panel;
  //   box-shadow: $mce-hover-flag-box-shadow-drop;
  //   background-color: $mce-hover-flag-background-color;
  // }
</style>
